import React, { useState } from "react";
import adminService from "@/services/api/admin.service";
import { CheckCircle, AlertCircle } from "lucide-react";

const CreateExpert = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    website: "",
    specialties: [],
    degrees: [{ name: "", institution: "", year: "" }],
    certifications: [{ name: "", issuingOrganization: "", issueDate: "" }],
    availableStartTime: "",
    availableEndTime: "",
    breakStartTime: "",
    breakEndTime: "",
    timezone: "Asia/Kolkata", // Default to India timezone
  });

  const [errors, setErrors] = useState({});
  const [successPopup, setSuccessPopup] = useState(false);
  const [error, setError] = useState("");

  // Timezone options most relevant to Maharashtra, India
  const timezoneOptions = [
    { value: "Asia/Kolkata", label: "India Standard Time (IST)" },
    { value: "Asia/Dubai", label: "Gulf Standard Time (GST)" },
    { value: "Asia/Kabul", label: "Afghanistan Time (AFT)" },

    { value: "Asia/Dhaka", label: "Bangladesh Standard Time (BST)" },
    { value: "Asia/Colombo", label: "Sri Lanka Standard Time (SLST)" },
    { value: "Asia/Kathmandu", label: "Nepal Time (NPT)" },
    { value: "Asia/Bangkok", label: "Indochina Time (ICT)" },
    { value: "Asia/Singapore", label: "Singapore Time (SGT)" },
  ];
  const validateWebsiteInput = (website) => {
    if (!website) return { isValid: true, message: "" }; // Empty field is valid

    const re =
      /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;

    if (!re.test(website)) {
      return { isValid: false, message: "Invalid website URL" };
    }
    return { isValid: true, message: "" };
  };
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    // Indian phone number validation (supports formats like +91xxxxxxxxxx, 91xxxxxxxxxx, 0xxxxxxxxxx)
    const re = /^(?:\+?91|0)?[6-9]\d{9}$/;
    return re.test(phone);
  };

  const validateWebsite = (website) => {
    if (!website) return true; // Optional field
    const re =
      /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
    return re.test(website);
  };

  const validateTime = (startTime, endTime) => {
    if (!startTime || !endTime) return true;
    return startTime < endTime;
  };

  const validateBreakTime = (breakStart, breakEnd, availStart, availEnd) => {
    if (!breakStart || !breakEnd) return true; // Breaks are optional
    if (breakStart >= breakEnd) return false;
    if (availStart && availEnd) {
      return breakStart >= availStart && breakEnd <= availEnd;
    }
    return true;
  };
  const validateNameInput = (name) => {
    if (!name.trim()) {
      return { isValid: false, message: "This field is required" };
    }

    // Regex to allow only alphabets, spaces, hyphens, and apostrophes (for names like O'Connor or Smith-Jones)
    const nameRegex = /^[A-Za-z\s'-]+$/;

    if (!nameRegex.test(name)) {
      return {
        isValid: false,
        message: "Only alphabetic characters are allowed",
      };
    }

    return { isValid: true, message: "" };
  };
  const validateEmailInput = (email) => {
    if (!email.trim()) {
      return { isValid: false, message: "Email is required" };
    }

    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(email)) {
      return {
        isValid: false,
        message: "Please enter a valid email address",
      };
    }

    return { isValid: true, message: "" };
  };

  // Enhanced phone validation with immediate feedback
  const validatePhoneInput = (phone) => {
    if (!phone.trim()) {
      return { isValid: false, message: "Phone number is required" };
    }

    // Indian phone number validation (supports formats like +91xxxxxxxxxx, 91xxxxxxxxxx, 0xxxxxxxxxx)
    const re = /^(?:\+?91|0)?[6-9]\d{9}$/;
    if (!re.test(phone)) {
      return {
        isValid: false,
        message: "Please enter a valid Indian phone number",
      };
    }

    return { isValid: true, message: "" };
  };
  const validateForm = () => {
    const newErrors = {};

    // Validate basic fields
    // Validate basic fields with the new validation
    const firstNameValidation = validateNameInput(formData.firstName);
    if (!firstNameValidation.isValid) {
      newErrors.firstName = firstNameValidation.message;
    }

    const lastNameValidation = validateNameInput(formData.lastName);
    if (!lastNameValidation.isValid) {
      newErrors.lastName = lastNameValidation.message;
    }
    const emailValidation = validateEmailInput(formData.email);
    if (!emailValidation.isValid) {
      newErrors.email = emailValidation.message;
    }

    const phoneValidation = validatePhoneInput(formData.phone);
    if (!phoneValidation.isValid) {
      newErrors.phone = phoneValidation.message;
    }
    if (formData.website) {
      const websiteValidation = validateWebsiteInput(formData.website);
      if (!websiteValidation.isValid) {
        newErrors.website = websiteValidation.message;
      }
    }

    if (!formData.timezone) newErrors.timezone = "Timezone is required";

    // Validate specialties
    if (formData.specialties.length === 0) {
      newErrors.specialties = "At least one specialty is required";
    }

    // Validate degrees
    formData.degrees.forEach((degree, index) => {
      if (!degree.name.trim()) {
        newErrors[`degrees[${index}].name`] = "Degree name is required";
      }
      if (!degree.institution.trim()) {
        newErrors[`degrees[${index}].institution`] = "Institution is required";
      }
      if (!degree.year) {
        newErrors[`degrees[${index}].year`] = "Year is required";
      } else if (
        isNaN(degree.year) ||
        parseInt(degree.year) < 1900 ||
        parseInt(degree.year) > new Date().getFullYear()
      ) {
        newErrors[`degrees[${index}].year`] = "Please enter a valid year";
      }
    });

    // Validate certifications
    formData.certifications.forEach((cert, index) => {
      if (!cert.name.trim()) {
        newErrors[`certifications[${index}].name`] =
          "Certification name is required";
      }
      if (!cert.issuingOrganization.trim()) {
        newErrors[`certifications[${index}].issuingOrganization`] =
          "Issuing organization is required";
      }
      if (!cert.issueDate) {
        newErrors[`certifications[${index}].issueDate`] =
          "Issue date is required";
      }
    });

    // Validate availability times
    if (!formData.availableStartTime) {
      newErrors.availableStartTime = "Start time is required";
    }
    if (!formData.availableEndTime) {
      newErrors.availableEndTime = "End time is required";
    }
    if (formData.availableStartTime && formData.availableEndTime) {
      if (
        !validateTime(formData.availableStartTime, formData.availableEndTime)
      ) {
        newErrors.availableEndTime = "End time must be after start time";
      }
    }

    // Validate break times if provided
    if (formData.breakStartTime || formData.breakEndTime) {
      if (!formData.breakStartTime) {
        newErrors.breakStartTime =
          "Break start time is required if break end time is provided";
      }
      if (!formData.breakEndTime) {
        newErrors.breakEndTime =
          "Break end time is required if break start time is provided";
      }
      if (formData.breakStartTime && formData.breakEndTime) {
        if (
          !validateBreakTime(
            formData.breakStartTime,
            formData.breakEndTime,
            formData.availableStartTime,
            formData.availableEndTime
          )
        ) {
          newErrors.breakTime =
            "Break time must be within available hours and end time must be after start time";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Live validation for different fields
    switch (name) {
      case "firstName":
      case "lastName":
        const nameValidation = validateNameInput(value);
        if (!nameValidation.isValid) {
          setErrors({ ...errors, [name]: nameValidation.message });
        } else {
          setErrors({ ...errors, [name]: null });
        }
        break;

      case "email":
        const emailValidation = validateEmailInput(value);
        if (!emailValidation.isValid) {
          setErrors({ ...errors, [name]: emailValidation.message });
        } else {
          setErrors({ ...errors, [name]: null });
        }
        break;

      case "phone":
        const phoneValidation = validatePhoneInput(value);
        if (!phoneValidation.isValid) {
          setErrors({ ...errors, [name]: phoneValidation.message });
        } else {
          setErrors({ ...errors, [name]: null });
        }
        break;

      case "website":
        const websiteValidation = validateWebsiteInput(value);
        if (!websiteValidation.isValid) {
          setErrors({ ...errors, [name]: websiteValidation.message });
        } else {
          setErrors({ ...errors, [name]: null });
        }
        break;

      default:
        // Clear error for other fields if they exist
        if (errors[name]) {
          setErrors({ ...errors, [name]: null });
        }
    }
  };
  const handleEmailBlur = () => {
    const validation = validateEmailInput(formData.email);
    if (!validation.isValid) {
      setErrors({ ...errors, email: validation.message });
    } else {
      setErrors({ ...errors, email: null });
    }
  };

  const handlePhoneBlur = () => {
    const validation = validatePhoneInput(formData.phone);
    if (!validation.isValid) {
      setErrors({ ...errors, phone: validation.message });
    } else {
      setErrors({ ...errors, phone: null });
    }
  };
  const handleWebsiteBlur = () => {
    const validation = validateWebsiteInput(formData.website);
    if (!validation.isValid) {
      setErrors({ ...errors, website: validation.message });
    } else {
      setErrors({ ...errors, website: null });
    }
  };
  const handleSpecialtiesChange = (e) => {
    const specialtiesArray = e.target.value
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s !== "");
    setFormData({ ...formData, specialties: specialtiesArray });

    if (errors.specialties) {
      setErrors({ ...errors, specialties: null });
    }
  };

  const handleDegreesChange = (index, field, value) => {
    const updatedDegrees = [...formData.degrees];
    updatedDegrees[index][field] = value;
    setFormData({ ...formData, degrees: updatedDegrees });

    if (errors[`degrees[${index}].${field}`]) {
      setErrors({ ...errors, [`degrees[${index}].${field}`]: null });
    }
  };

  const handleCertificationsChange = (index, field, value) => {
    const updatedCertifications = [...formData.certifications];
    updatedCertifications[index][field] = value;
    setFormData({ ...formData, certifications: updatedCertifications });

    if (errors[`certifications[${index}].${field}`]) {
      setErrors({ ...errors, [`certifications[${index}].${field}`]: null });
    }
  };

  const addDegree = () => {
    setFormData({
      ...formData,
      degrees: [...formData.degrees, { name: "", institution: "", year: "" }],
    });
  };

  const removeDegree = (index) => {
    if (formData.degrees.length > 1) {
      const updatedDegrees = [...formData.degrees];
      updatedDegrees.splice(index, 1);
      setFormData({ ...formData, degrees: updatedDegrees });
    }
  };

  const addCertification = () => {
    setFormData({
      ...formData,
      certifications: [
        ...formData.certifications,
        { name: "", issuingOrganization: "", issueDate: "" },
      ],
    });
  };

  const removeCertification = (index) => {
    if (formData.certifications.length > 1) {
      const updatedCertifications = [...formData.certifications];
      updatedCertifications.splice(index, 1);
      setFormData({ ...formData, certifications: updatedCertifications });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset previous errors
    setError("");

    // Validate form
    const isValid = validateForm();
    if (!isValid) return;

    try {
      await adminService.createExpert(formData);
      setSuccessPopup(true);

      // Reset form after successful submission
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        website: "",
        specialties: [],
        degrees: [{ name: "", institution: "", year: "" }],
        certifications: [{ name: "", issuingOrganization: "", issueDate: "" }],
        availableStartTime: "",
        availableEndTime: "",
        breakStartTime: "",
        breakEndTime: "",
        timezone: "Asia/Kolkata",
      });
    } catch (err) {
      setError(err.response?.data?.message || "An error occurred");
    }
  };

  // Helper function to display field error
  const getFieldError = (fieldName) => {
    return errors[fieldName] ? (
      <p className="mt-1 text-xs text-red-500">{errors[fieldName]}</p>
    ) : null;
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h1 className="text-2xl font-semibold text-gray-800 mb-6">
          Create New Expert Profile
        </h1>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md flex items-center">
            <AlertCircle className="mr-2 h-5 w-5" />
            {error}
          </div>
        )}

        {errors.breakTime && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md flex items-center">
            <AlertCircle className="mr-2 h-5 w-5" />
            {errors.breakTime}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name*
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border ${
                  errors.firstName ? "border-red-300" : "border-gray-300"
                } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="Enter your Name"
              />
              {getFieldError("firstName")}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name*
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border ${
                  errors.lastName ? "border-red-300" : "border-gray-300"
                } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="Enter your Last Name"
              />
              {getFieldError("lastName")}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email*
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleEmailBlur}
                className={`w-full px-3 py-2 border ${
                  errors.email ? "border-red-300" : "border-gray-300"
                } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="Enter your Email"
              />
              {getFieldError("email")}
              <p className="mt-1 text-xs text-gray-500">
                Format: example@domain.com
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone*
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                onBlur={handlePhoneBlur}
                className={`w-full px-3 py-2 border ${
                  errors.phone ? "border-red-300" : "border-gray-300"
                } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="+91xxxxxxxxxx"
              />
              {getFieldError("phone")}
              <p className="mt-1 text-xs text-gray-500">
                Format: +91xxxxxxxxxx or 91xxxxxxxxxx
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Website
              </label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                onBlur={handleWebsiteBlur}
                className={`w-full px-3 py-2 border ${
                  errors.website ? "border-red-300" : "border-gray-300"
                } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="https://example.com"
              />
              {getFieldError("website")}
              <p className="mt-1 text-xs text-gray-500">
                Format: https://example.com or www.example.com
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Timezone*
              </label>
              <select
                name="timezone"
                value={formData.timezone}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border ${
                  errors.timezone ? "border-red-300" : "border-gray-300"
                } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                {timezoneOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {getFieldError("timezone")}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h2 className="text-lg font-medium text-gray-800 mb-4">
              Professional Information
            </h2>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Specialties*
              </label>
              <input
                type="text"
                name="specialties"
                value={formData.specialties.join(", ")}
                onChange={handleSpecialtiesChange}
                className={`w-full px-3 py-2 border ${
                  errors.specialties ? "border-red-300" : "border-gray-300"
                } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="Enter comma-separated specialties"
              />
              {getFieldError("specialties")}
              <p className="mt-1 text-xs text-gray-500">
                Example: Psychology, Counseling, CBT
              </p>
            </div>

            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Degrees*
                </label>
                <button
                  type="button"
                  onClick={addDegree}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  + Add Degree
                </button>
              </div>

              {formData.degrees.map((degree, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-md mb-3">
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Degree {index + 1}
                    </span>
                    {formData.degrees.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeDegree(index)}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Degree Name*
                      </label>
                      <input
                        type="text"
                        value={degree.name}
                        onChange={(e) =>
                          handleDegreesChange(index, "name", e.target.value)
                        }
                        className={`w-full px-3 py-2 border ${
                          errors[`degrees[${index}].name`]
                            ? "border-red-300"
                            : "border-gray-300"
                        } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                      {getFieldError(`degrees[${index}].name`)}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Institution*
                      </label>
                      <input
                        type="text"
                        value={degree.institution}
                        onChange={(e) =>
                          handleDegreesChange(
                            index,
                            "institution",
                            e.target.value
                          )
                        }
                        className={`w-full px-3 py-2 border ${
                          errors[`degrees[${index}].institution`]
                            ? "border-red-300"
                            : "border-gray-300"
                        } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                      {getFieldError(`degrees[${index}].institution`)}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Year*
                      </label>
                      <input
                        type="number"
                        value={degree.year}
                        onChange={(e) =>
                          handleDegreesChange(index, "year", e.target.value)
                        }
                        className={`w-full px-3 py-2 border ${
                          errors[`degrees[${index}].year`]
                            ? "border-red-300"
                            : "border-gray-300"
                        } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        min="1900"
                        max={new Date().getFullYear()}
                      />
                      {getFieldError(`degrees[${index}].year`)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Certifications*
                </label>
                <button
                  type="button"
                  onClick={addCertification}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  + Add Certification
                </button>
              </div>

              {formData.certifications.map((certification, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-md mb-3">
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Certification {index + 1}
                    </span>
                    {formData.certifications.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCertification(index)}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Name*
                      </label>
                      <input
                        type="text"
                        value={certification.name}
                        onChange={(e) =>
                          handleCertificationsChange(
                            index,
                            "name",
                            e.target.value
                          )
                        }
                        className={`w-full px-3 py-2 border ${
                          errors[`certifications[${index}].name`]
                            ? "border-red-300"
                            : "border-gray-300"
                        } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                      {getFieldError(`certifications[${index}].name`)}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Issuing Organization*
                      </label>
                      <input
                        type="text"
                        value={certification.issuingOrganization}
                        onChange={(e) =>
                          handleCertificationsChange(
                            index,
                            "issuingOrganization",
                            e.target.value
                          )
                        }
                        className={`w-full px-3 py-2 border ${
                          errors[`certifications[${index}].issuingOrganization`]
                            ? "border-red-300"
                            : "border-gray-300"
                        } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                      {getFieldError(
                        `certifications[${index}].issuingOrganization`
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Issue Date*
                      </label>
                      <input
                        type="date"
                        value={certification.issueDate}
                        onChange={(e) =>
                          handleCertificationsChange(
                            index,
                            "issueDate",
                            e.target.value
                          )
                        }
                        className={`w-full px-3 py-2 border ${
                          errors[`certifications[${index}].issueDate`]
                            ? "border-red-300"
                            : "border-gray-300"
                        } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        max={new Date().toISOString().split("T")[0]}
                      />
                      {getFieldError(`certifications[${index}].issueDate`)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h2 className="text-lg font-medium text-gray-800 mb-4">
              Availability
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Available Start Time*
                </label>
                <input
                  type="time"
                  name="availableStartTime"
                  value={formData.availableStartTime}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${
                    errors.availableStartTime
                      ? "border-red-300"
                      : "border-gray-300"
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {getFieldError("availableStartTime")}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Available End Time*
                </label>
                <input
                  type="time"
                  name="availableEndTime"
                  value={formData.availableEndTime}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${
                    errors.availableEndTime
                      ? "border-red-300"
                      : "border-gray-300"
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {getFieldError("availableEndTime")}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Break Start Time
                </label>
                <input
                  type="time"
                  name="breakStartTime"
                  value={formData.breakStartTime}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${
                    errors.breakStartTime ? "border-red-300" : "border-gray-300"
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {getFieldError("breakStartTime")}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Break End Time
                </label>
                <input
                  type="time"
                  name="breakEndTime"
                  value={formData.breakEndTime}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${
                    errors.breakEndTime ? "border-red-300" : "border-gray-300"
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {getFieldError("breakEndTime")}
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-6">
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              Create Expert Profile
            </button>
          </div>
        </form>
      </div>

      {/* Success Popup */}
      {successPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg text-center">
            <CheckCircle className="text-green-500 w-16 h-16 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800">
              Expert Created Successfully!
            </h2>
            <button
              onClick={() => setSuccessPopup(false)}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateExpert;
